// Iter 132 — Global axios interceptor for Safari ITP / iCloud Private Relay.
//
// Safari (iOS especially) silently strips first-party HttpOnly cookies in
// some configurations — the symptom is "login succeeds, then the very next
// /me request 401s and the user bounces back to /login". To make the portal
// reliable on Safari we ALSO accept an Authorization: Bearer JWT.
//
// Strategy:
//   • Backend keeps setting the HttpOnly cookie (browsers that respect it
//     continue using that — strictly safer, not readable by JS).
//   • Login endpoint ALSO returns `access_token` in the response body.
//   • This file:
//       1. Stores the token in localStorage on login.
//       2. Adds an axios *request* interceptor that attaches
//          `Authorization: Bearer <token>` to every outbound request.
//       3. Adds an axios *response* interceptor that clears the stored token
//          on 401 so a stale token doesn't keep redirecting the user.
//
// Trade-off: localStorage tokens are JS-readable so a successful XSS could
// steal them. We accept that trade-off only because Safari already breaks
// the (safer) cookie flow for some users — a working login is more important
// than the marginal security gain of cookie-only when XSS prevention is
// already enforced via React's escaping + Tailwind + no innerHTML.
import axios from 'axios';

const TOKEN_KEY = 'i3_access_token';

export const getStoredToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); }
  catch { return null; }  // private-mode Safari can throw on localStorage access
};

export const setStoredToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* ignore — fall back to cookie-only */ }
};

export const clearStoredToken = () => setStoredToken(null);

// One-time setup. Idempotent so HMR doesn't double-install.
let installed = false;

export function installAuthInterceptors() {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    const t = getStoredToken();
    if (t) {
      config.headers = config.headers || {};
      // Don't clobber an explicit Authorization passed by the caller.
      if (!config.headers.Authorization && !config.headers.authorization) {
        config.headers.Authorization = `Bearer ${t}`;
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (r) => {
      // Iter 132 — Any response that carries an `access_token` field (login,
      // TOTP verify, force-change-password, future refresh endpoints…) gets
      // its token stashed for the bearer fallback. Done globally so we don't
      // need to remember to do it inside every component.
      const t = r?.data?.access_token;
      if (t && typeof t === 'string' && t.split('.').length === 3) {
        setStoredToken(t);
      }
      return r;
    },
    (err) => {
      if (err?.response?.status === 401) {
        // Stale token — wipe it so the app can recover via /login redirect.
        clearStoredToken();
      }
      return Promise.reject(err);
    },
  );
}
